/**
 * AssessmentPreview Component - Preview do Assessment Completado
 * @module features/results/components/AssessmentPreview
 *
 * Componente que exibe um card com resumo do assessment realizado,
 * incluindo nome do usuário, tipo, data/hora e métricas opcionais.
 *
 * T001-FE: Tela de Resumo com Resultados do Assessment
 *
 * Features:
 * - Card com dados do usuário
 * - Badge para tipo de assessment
 * - Data/hora formatada em pt-BR
 * - Métricas opcionais (perguntas, duração)
 * - Semântica e acessibilidade
 */

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Calendar, Clock, MessageSquare, User } from 'lucide-react';
import type { AssessmentPreviewProps } from '../types';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Formata data para exibição em pt-BR
 */
function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Formata hora para exibição
 */
function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Retorna label do tipo de assessment
 */
function getAssessmentTypeLabel(type: 'tech' | 'governance'): string {
  return type === 'tech' ? 'Performance Tech' : 'Governança de Produtos';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Componente de preview do assessment completado
 *
 * @param props - Props do componente
 * @param props.summary - Resumo do assessment
 *
 * @example
 * ```tsx
 * <AssessmentPreview
 *   summary={{
 *     userName: 'Maria Silva',
 *     assessmentType: 'tech',
 *     completedAt: new Date(),
 *     sessionId: 'session-123',
 *   }}
 * />
 * ```
 */
export function AssessmentPreview({ summary }: AssessmentPreviewProps) {
  const { userName, assessmentType, completedAt, questionsAnswered, durationMinutes } = summary;

  return (
    <Card data-testid="assessment-preview" className={cn('rounded-lg border bg-card shadow-sm')}>
      <CardContent data-testid="assessment-preview-content" className="p-6">
        {/* Header with name and badge */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <User
              data-testid="user-icon"
              className="h-5 w-5 text-muted-foreground"
              aria-hidden="true"
            />
            <h3 className="text-lg font-semibold text-foreground">{userName}</h3>
          </div>

          <Badge data-testid="assessment-type-badge" variant="secondary" className="w-fit">
            {getAssessmentTypeLabel(assessmentType)}
          </Badge>
        </div>

        {/* Info Grid */}
        <div data-testid="assessment-info-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Date and Time */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar data-testid="calendar-icon" className="h-4 w-4" aria-hidden="true" />
            <time data-testid="completion-datetime" dateTime={completedAt.toISOString()}>
              {formatDate(completedAt)}, {formatTime(completedAt)}
            </time>
          </div>

          {/* Duration (optional) */}
          {durationMinutes !== undefined && (
            <div
              data-testid="duration-info"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Clock data-testid="clock-icon" className="h-4 w-4" aria-hidden="true" />
              <span>{durationMinutes} min de duração</span>
            </div>
          )}

          {/* Questions (optional) */}
          {questionsAnswered !== undefined && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              <span>{questionsAnswered} perguntas respondidas</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default AssessmentPreview;
